import React from 'react';
import type {Task} from '@rocket-ui/lk-api';
import {fetchTask} from '@rocket-ui/lk-api';
import {useQueryClient} from 'react-query';
import type {OrderActionsView} from './types';
import {refreshOrdersTableFirstPage} from './refreshOrdersTableFirstPage';

const POLL_INTERVAL_MS = 5_000;

export function usePollTaskOnAcceptanceSign() {
	const queryClient = useQueryClient();
	const pollIntervalsRef = React.useRef<Map<string, number>>(new Map());
	const pollViewRef = React.useRef<Map<string, OrderActionsView>>(new Map());
	const initialStatusRef = React.useRef<Map<string, Task['status']>>(new Map());

	const stopPolling = React.useCallback((taskId: string | number) => {
		const id = String(taskId);
		const intervalId = pollIntervalsRef.current.get(id);

		if (intervalId != null) {
			window.clearInterval(intervalId);
			pollIntervalsRef.current.delete(id);
		}

		pollViewRef.current.delete(id);
		initialStatusRef.current.delete(id);
	}, []);

	const pollTask = React.useCallback(
		async (taskId: string | number, view: OrderActionsView) => {
			const id = String(taskId);
			const initialStatus = initialStatusRef.current.get(id);

			try {
				const {data} = await fetchTask({id});

				if (view === 'order') {
					queryClient.setQueryData(['task', id], data);
					queryClient.setQueryData(['task', String(taskId)], data);
				}

				if (initialStatus != null && data.status !== initialStatus) {
					// Обновляем и детальную, и карточки в списке.
					await refreshOrdersTableFirstPage(queryClient);

					stopPolling(id);
				}
			} catch {
				// keep polling on transient errors
			}
		},
		[queryClient, stopPolling],
	);

	const startPolling = React.useCallback(
		(taskId: string | number, view: OrderActionsView = 'order', initialStatus?: Task['status']) => {
			const id = String(taskId);
			stopPolling(id);
			pollViewRef.current.set(id, view);

			if (initialStatus != null) {
				initialStatusRef.current.set(id, initialStatus);
			}

			void pollTask(id, view);
			const intervalId = window.setInterval(() => {
				const currentView = pollViewRef.current.get(id) ?? view;
				void pollTask(id, currentView);
			}, POLL_INTERVAL_MS);
			pollIntervalsRef.current.set(id, intervalId);
		},
		[pollTask, stopPolling],
	);

	React.useEffect(() => {
		const intervals = pollIntervalsRef.current;

		return () => {
			intervals.forEach((intervalId) => window.clearInterval(intervalId));
			intervals.clear();
			pollViewRef.current.clear();
			initialStatusRef.current.clear();
		};
	}, []);

	return {startPolling, stopPolling};
}
